<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useJobs } from "../composables/useJobs";
import {
  DuplicatePointer,
  MAX_UPLOAD_BYTES,
  isYouTubeUrl,
  useUpload,
} from "../composables/useUpload";
import { useToast } from "../composables/useToast";
import UploadProgress from "../components/UploadProgress.vue";
import DropZone from "../components/DropZone.vue";
import TraceReference from "../components/TraceReference.vue";

const router = useRouter();
const { loadJobs } = useJobs();
const upload = useUpload();
const { showSuccess, showError } = useToast();

type SourceMode = "file" | "url";

const sourceMode = ref<SourceMode>("file");
const jobName = ref("");
const selectedFile = ref<File | null>(null);
const sourceUrl = ref("");
const validationError = ref<string | null>(null);

// 409 YOUTUBE_DUPLICATE prompt — null when not shown. See
// docs/design/youtube-reingest-dedupe-one-pager.md. Two user actions:
//   "Open existing" → router push to /jobs/<existing_id>
//   "Run again"     → resubmit POST /jobs with force_new=true
const duplicate = ref<DuplicatePointer | null>(null);

function formatExistingDate(iso: string): string {
  // The server hands us ISO-8601; render in the user's locale. Bare
  // ``new Date(...)`` already produces a human-friendly format; we go
  // through ``toLocaleDateString`` so it doesn't carry a time-of-day
  // that's irrelevant to the prompt's question ("you analyzed this on
  // X — open it?"). Falls back to the raw ISO on invalid input
  // (defensive — the server's value is always well-formed).
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function onDedupeModalKey(event: KeyboardEvent) {
  if (event.key === "Escape" && duplicate.value) {
    duplicate.value = null;
  }
}

watch(duplicate, (cur) => {
  if (cur) {
    document.addEventListener("keydown", onDedupeModalKey);
  } else {
    document.removeEventListener("keydown", onDedupeModalKey);
  }
});
onBeforeUnmount(() => document.removeEventListener("keydown", onDedupeModalKey));

// Switching modes clears the other tab's selection so a stale file
// (or stale URL) can't accidentally submit via the wrong path.
// URL-tab no longer pre-fills jobName — when left empty, the server
// fills from a YouTube metadata probe (sanitized title), falling
// back to "YouTube video" only on probe failure. See
// docs/design/youtube-job-name-from-title-one-pager.md.
watch(sourceMode, () => {
  selectedFile.value = null;
  sourceUrl.value = "";
  validationError.value = null;
});

/**
 * Decimal GB to match how cloud storage caps (including B2's 5 GB
 * single-PUT cap) are documented. Don't switch to GiB without also
 * updating MAX_UPLOAD_BYTES — they have to match the same scale or the
 * "too large" message shows a different number than the constant.
 */
function formatGB(n: number): string {
  return `${(n / (1000 * 1000 * 1000)).toFixed(1)} GB`;
}

function formatBytes(n: number): string {
  if (n < 1000 * 1000) return `${(n / 1000).toFixed(1)} KB`;
  if (n < 1000 * 1000 * 1000) return `${(n / (1000 * 1000)).toFixed(1)} MB`;
  return formatGB(n);
}

/**
 * Auto-fill the job-name input from the filename, stripping the
 * extension. Only fires when the user hasn't typed a name yet — a
 * user who picked a file and then started typing shouldn't have
 * their input overwritten by a second file pick.
 */
function autoFillName(file: File): void {
  if (jobName.value.trim().length > 0) return;
  const stem = file.name.replace(/\.[^.]+$/, "");
  jobName.value = stem;
}

/**
 * Validate locally before any network call. Two reasons:
 *
 * 1. Type: we re-check `file.type` and the `.mp4` extension because
 *    drag-and-drop bypasses the file-picker's accept filter, and some
 *    browsers leave `type` as the empty string for files dropped from
 *    odd sources.
 *
 * 2. Size: enforce `MAX_UPLOAD_BYTES` (5 GB decimal — matches B2's
 *    actual single-PUT cap) locally so users get an immediate "too
 *    large" error instead of B2's late-stage rejection.
 */
function onFile(file: File) {
  validationError.value = null;
  const isMp4 =
    file.type === "video/mp4" || file.name.toLowerCase().endsWith(".mp4");
  if (!isMp4) {
    validationError.value = `Only MP4 video files are accepted (got "${file.name}").`;
    selectedFile.value = null;
    return;
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    validationError.value = `File is too large (${formatGB(file.size)}). Max is ${formatGB(MAX_UPLOAD_BYTES)}.`;
    selectedFile.value = null;
    return;
  }
  selectedFile.value = file;
  autoFillName(file);
}

const isUploading = computed(
  () =>
    upload.state.value !== "idle" &&
    upload.state.value !== "completed" &&
    upload.state.value !== "error" &&
    upload.state.value !== "cancelled",
);

const showProgressBar = computed(
  () => upload.state.value === "uploading" || upload.state.value === "finalizing",
);

const statusLabel = computed(() => {
  switch (upload.state.value) {
    case "creating":
      return "Creating job";
    case "signing":
      return "Preparing upload";
    case "uploading":
      return "Uploading";
    case "finalizing":
      return "Finalizing";
    default:
      return "Uploading";
  }
});

const canSubmit = computed(() => {
  if (isUploading.value) return false;
  // URL-mode allows empty jobName — server fills from YouTube title.
  // File-mode still requires it (no analog metadata source).
  if (sourceMode.value === "file") {
    if (jobName.value.trim().length === 0) return false;
    return selectedFile.value !== null;
  }
  return isYouTubeUrl(sourceUrl.value);
});

function onUrlBlur() {
  // Only flag invalid once the user has typed something meaningful —
  // an empty box shouldn't show an error during normal typing.
  if (sourceMode.value !== "url") return;
  const v = sourceUrl.value.trim();
  if (v.length > 0 && !isYouTubeUrl(v)) {
    validationError.value =
      "Only YouTube URLs are supported (youtube.com/watch?v=… or youtu.be/…).";
  } else {
    validationError.value = null;
  }
}

async function submitUrl(forceNew = false) {
  const result = await upload.startWithUrl(
    sourceUrl.value.trim(),
    jobName.value.trim(),
    forceNew,
  );
  if (result.ok) {
    showSuccess("Job created — processing started.");
    await loadJobs();
    router.push({ name: "job-detail", params: { id: result.jobId } });
    return;
  }
  if ("duplicate" in result) {
    // Server says we already analyzed this video — pop the dedupe modal.
    // The modal owns the next user action; don't show an error toast.
    duplicate.value = result.duplicate;
    return;
  }
  if (upload.error.value) {
    showError(`Upload failed: ${upload.error.value}`);
  }
}

async function onSubmit() {
  if (!canSubmit.value) return;

  if (sourceMode.value === "url") {
    await submitUrl(false);
    return;
  }
  if (!selectedFile.value) return;
  const result = await upload.start(
    selectedFile.value,
    jobName.value.trim(),
  );
  if (result.ok) {
    showSuccess("Job created — processing started.");
    await loadJobs();
    router.push({ name: "job-detail", params: { id: result.jobId } });
  } else if (upload.error.value) {
    showError(`Upload failed: ${upload.error.value}`);
  }
}

function onOpenExisting() {
  const dup = duplicate.value;
  if (!dup) return;
  duplicate.value = null;
  router.push({ name: "job-detail", params: { id: dup.existingJobId } });
}

async function onRunAgain() {
  duplicate.value = null;
  await submitUrl(true);
}

function dismissDedupe() {
  duplicate.value = null;
}

function clearFile() {
  selectedFile.value = null;
  validationError.value = null;
}

function retry() {
  upload.reset();
}
</script>

<template>
  <section class="p-8 max-w-2xl mx-auto">
    <h2 class="text-2xl font-semibold text-text-primary mb-6">Create new job</h2>

    <form
      data-testid="new-job-form"
      class="space-y-5"
      @submit.prevent="onSubmit"
    >
      <div role="tablist" class="inline-flex rounded-md border border-border overflow-hidden">
        <button
          type="button"
          role="tab"
          :aria-selected="sourceMode === 'file'"
          data-testid="source-tab-file"
          :disabled="isUploading"
          class="px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed"
          :class="
            sourceMode === 'file'
              ? 'bg-primary text-white'
              : 'bg-bg-secondary text-text-secondary hover:bg-bg-hover'
          "
          @click="sourceMode = 'file'"
        >
          Upload file
        </button>
        <button
          type="button"
          role="tab"
          :aria-selected="sourceMode === 'url'"
          data-testid="source-tab-url"
          :disabled="isUploading"
          class="px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed"
          :class="
            sourceMode === 'url'
              ? 'bg-primary text-white'
              : 'bg-bg-secondary text-text-secondary hover:bg-bg-hover'
          "
          @click="sourceMode = 'url'"
        >
          Paste YouTube URL
        </button>
      </div>

      <!-- File-upload mode -->
      <template v-if="sourceMode === 'file'">
        <div v-if="!selectedFile">
          <DropZone
            accept="video/mp4,.mp4"
            :disabled="isUploading"
            :hint="`MP4, up to ${formatGB(MAX_UPLOAD_BYTES)}`"
            @file="onFile"
          />
          <p
            v-if="validationError"
            data-testid="validation-error"
            class="mt-2 text-sm text-error"
          >
            {{ validationError }}
          </p>
        </div>

        <div
          v-else
          data-testid="selected-file-row"
          class="rounded-md border border-border bg-bg-secondary px-4 py-3 flex items-center justify-between gap-4"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium text-text-primary truncate">{{ selectedFile.name }}</p>
            <p class="text-xs text-text-tertiary">{{ formatBytes(selectedFile.size) }}</p>
          </div>
          <button
            type="button"
            data-testid="clear-file-button"
            :disabled="isUploading"
            class="shrink-0 text-sm text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="clearFile"
          >
            Change
          </button>
        </div>
      </template>

      <!-- URL-ingest mode (Phase 2). YouTube ingest is best-effort —
           UI copy doesn't promise reliability; failed jobs surface
           the fallback in JobDetailView. -->
      <template v-else>
        <div>
          <label class="block text-sm font-medium text-text-primary mb-1" for="source-url-input">
            YouTube URL
          </label>
          <input
            id="source-url-input"
            v-model="sourceUrl"
            type="url"
            data-testid="source-url-input"
            :disabled="isUploading"
            class="w-full rounded-md border border-border bg-bg-input px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:bg-bg-secondary disabled:cursor-not-allowed transition-colors"
            placeholder="https://www.youtube.com/watch?v=…"
            @blur="onUrlBlur"
          />
          <p class="mt-1 text-xs text-text-tertiary">
            Some videos can't be downloaded directly. If this fails, upload the file instead.
          </p>
          <p
            v-if="validationError"
            data-testid="validation-error"
            class="mt-2 text-sm text-error"
          >
            {{ validationError }}
          </p>
        </div>
      </template>

      <div>
        <label class="block text-sm font-medium text-text-primary mb-1" for="job-name-input">
          Job name
          <span
            v-if="sourceMode === 'url'"
            class="text-text-tertiary font-normal"
          >(optional)</span>
        </label>
        <input
          id="job-name-input"
          v-model="jobName"
          type="text"
          data-testid="job-name-input"
          :disabled="isUploading"
          class="w-full rounded-md border border-border bg-bg-input px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:bg-bg-secondary disabled:cursor-not-allowed transition-colors"
          :placeholder="
            sourceMode === 'url'
              ? 'Auto-filled from YouTube title'
              : 'e.g., USA vs Japan, Q3 2026'
          "
        />
      </div>

      <div v-if="showProgressBar">
        <UploadProgress
          :percent="upload.progress.value"
          :bytes-sent="upload.bytesSent.value"
          :total-bytes="upload.totalBytes.value"
          :bytes-per-sec="upload.bytesPerSec.value"
          :label="statusLabel"
        />
      </div>

      <div
        v-if="upload.state.value === 'error' && upload.error.value"
        data-testid="upload-error"
        class="rounded-md border border-error-border bg-error-bg px-4 py-3 text-sm text-error"
      >
        <div>Upload failed: {{ upload.error.value }}</div>
        <TraceReference :trace-id="upload.errorTraceId.value" />
      </div>

      <div
        v-else-if="upload.state.value === 'cancelled'"
        data-testid="upload-cancelled"
        class="rounded-md border border-border bg-bg-secondary px-4 py-3 text-sm text-text-secondary"
      >
        Upload cancelled.
      </div>

      <div class="flex gap-2 pt-2">
        <button
          v-if="upload.state.value === 'error' || upload.state.value === 'cancelled'"
          type="button"
          data-testid="retry-button"
          class="rounded-md border border-border bg-bg-main px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-hover transition-colors"
          @click="retry"
        >
          Retry
        </button>
        <button
          type="submit"
          data-testid="submit-button"
          :disabled="!canSubmit"
          class="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover transition-colors disabled:bg-text-tertiary disabled:cursor-not-allowed"
        >
          {{ isUploading ? statusLabel + "…" : "Submit" }}
        </button>
      </div>
    </form>

    <!--
      YouTube re-ingest dedupe modal (server returns 409 + code
      YOUTUBE_DUPLICATE when the same user already has a completed
      job for the same youtube_video_id). Two equal-weight actions —
      neither is destructive, so we don't reuse ConfirmDialog's
      primary/cancel chrome. See
      docs/design/youtube-reingest-dedupe-one-pager.md.
    -->
    <Teleport to="body">
      <div
        v-if="duplicate"
        data-testid="dedupe-modal"
        class="fixed inset-0 z-40 flex items-center justify-center p-4"
        style="background-color: var(--color-backdrop)"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dedupe-modal-title"
        @click.self="dismissDedupe"
      >
        <div class="relative w-full max-w-md rounded-lg bg-bg-main shadow-xl p-6">
          <h2
            id="dedupe-modal-title"
            data-testid="dedupe-modal-title"
            class="text-lg font-semibold text-text-primary mb-3"
          >
            Already analyzed
          </h2>
          <p
            data-testid="dedupe-modal-body"
            class="text-sm text-text-secondary mb-6"
          >
            You've already analyzed
            <span class="font-medium text-text-primary">"{{ duplicate.existingJobName }}"</span>
            on {{ formatExistingDate(duplicate.existingCreatedAt) }}.
            Open it, or run a fresh analysis on this video again?
          </p>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              data-testid="dedupe-modal-run-again"
              class="rounded-md border border-border bg-bg-main px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-hover transition-colors"
              @click="onRunAgain"
            >
              Run again
            </button>
            <button
              type="button"
              data-testid="dedupe-modal-open-existing"
              class="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover transition-colors"
              @click="onOpenExisting"
            >
              Open existing job
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </section>
</template>
